/**
 * Reward modal for when a user earns a new kiwi skin by completing daily goals.
 * Classic card reveal with radial glow, confetti, and shimmer text.
 */

import { useEffect, useState } from "react";
import { KiwiSkinRenderer } from "./KiwiSkinRenderer";
import { RARITY_COLORS } from "./kiwiRarityColors";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// ── Keyframe animations ─────────────────────────────────────────────────

const KEYFRAMES = `
@keyframes reward-glow-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.15); }
}
@keyframes reward-float-up {
  0% { opacity: 0; transform: translateY(30px) scale(0.7); }
  60% { opacity: 1; transform: translateY(-5px) scale(1.05); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes reward-shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes reward-confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(120px) rotate(360deg); opacity: 0; }
}
@keyframes reward-text-reveal {
  0% { opacity: 0; transform: translateY(10px); letter-spacing: 0.3em; }
  100% { opacity: 1; transform: translateY(0); letter-spacing: 0.1em; }
}
@keyframes reward-badge-pop {
  0% { opacity: 0; transform: scale(0); }
  60% { transform: scale(1.3); }
  100% { opacity: 1; transform: scale(1); }
}
`;

function AnimationStyles() {
  return <style>{KEYFRAMES}</style>;
}

// ── Confetti particles ──────────────────────────────────────────────────

function ConfettiParticles({ color, count = 20 }: { color: string; count?: number }) {
  const particles = Array.from({ length: count }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 1.5;
    const size = 4 + Math.random() * 6;
    const shades = [color, `${color}cc`, `${color}88`, `${color}dd`, `${color}aa`, `${color}66`];
    const c = shades[i % shades.length];
    return (
      <div
        key={i}
        style={{
          position: "absolute",
          left: `${left}%`,
          top: 0,
          width: size,
          height: size,
          borderRadius: i % 3 === 0 ? "50%" : i % 3 === 1 ? "2px" : "0",
          background: c,
          animation: `reward-confetti-fall ${1.5 + Math.random()}s ease-in ${delay}s forwards`,
          opacity: 0,
          zIndex: 10,
        }}
      />
    );
  });
  return <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>{particles}</div>;
}

// ── Rarity badge ────────────────────────────────────────────────────────

function RarityBadge({ rarity, delay = 0 }: { rarity: string; delay?: number }) {
  const color = RARITY_COLORS[rarity as keyof typeof RARITY_COLORS] ?? "#999";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 14px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#fff",
        background: `linear-gradient(135deg, ${color}, ${color}cc)`,
        boxShadow: `0 0 16px ${color}66`,
        animation: `reward-badge-pop 0.5s ease-out ${delay}s both`,
      }}
    >
      {rarity}
    </span>
  );
}

// ── Props ───────────────────────────────────────────────────────────────

export interface KiwiRewardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skin: KiwiSkinConfig;
  message?: string;
}

// ── Classic Card Reveal ─────────────────────────────────────────────────

export function KiwiRewardClassic({ open, onOpenChange, skin, message = "Daily goals complete!" }: KiwiRewardProps) {
  const rarityColor = RARITY_COLORS[skin.rarity];
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (open) {
      setRevealed(false);
      const t = setTimeout(() => setRevealed(true), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="sm:max-w-sm overflow-hidden">
        <AnimationStyles />
        {revealed && <ConfettiParticles color={rarityColor} count={24} />}

        <DialogHeader className="items-center pt-2">
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.6,
              animation: "reward-text-reveal 0.6s ease-out 0.2s both",
            }}
          >
            {message}
          </p>
          <DialogTitle
            style={{
              fontSize: 18,
              fontWeight: 800,
              animation: "reward-text-reveal 0.6s ease-out 0.4s both",
            }}
          >
            New Kiwi Unlocked!
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-4">
          {/* Radial glow background — solid center fading out */}
          <div
            style={{
              position: "relative",
              width: 200,
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "reward-float-up 0.8s ease-out 0.3s both",
            }}
          >
            {/* Pulsing outer glow */}
            <div
              style={{
                position: "absolute",
                inset: -80,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${rarityColor}30 0%, ${rarityColor}12 40%, transparent 70%)`,
                animation: "reward-glow-pulse 2s ease-in-out infinite",
              }}
            />
            {/* Core gradient */}
            <div
              style={{
                position: "absolute",
                inset: -40,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${rarityColor}44 0%, ${rarityColor}20 40%, transparent 72%)`,
              }}
            />
            <div style={{ position: "relative", zIndex: 2, width: 112, height: 112 }}>
              <KiwiSkinRenderer config={skin} className="size-full" />
            </div>
          </div>

          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: rarityColor,
              animation: "reward-float-up 0.6s ease-out 0.8s both",
            }}
          >
            {skin.name}
          </div>

          <RarityBadge rarity={skin.rarity} delay={1} />
        </div>

        <DialogFooter>
          <Button className="w-full" onClick={() => onOpenChange(false)}>
            Awesome!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
